import { useEffect, useState } from "react";
import { create } from "zustand";
import { SUPPORTED_END, SUPPORTED_START, type BodyId } from "@solar/ephemeris";

export type Mode = "model" | "real";
export type Lang = "zh" | "en";
export interface Layers { orbit: boolean; axis: boolean; label: boolean; moonOrbit: boolean; trail: boolean; auto: boolean }
/** 可隐藏的对象：天体 + 两个粒子带（小行星带、柯伊伯带）。 */
export type HideId = BodyId | "belt" | "kuiper";
export type Quality = "auto" | "low" | "medium" | "high";
export type EffectiveQuality = Exclude<Quality, "auto">;

const clamp = (ms: number) => Math.min(SUPPORTED_END, Math.max(SUPPORTED_START, ms));

/** 默认可见：太阳、八大行星、月球、冥王星；其他矮行星默认隐藏（需求：默认显示范围）。 */
export const DEFAULT_HIDDEN: HideId[] = ["ceres", "haumea", "makemake", "eris", "halley", "belt", "kuiper"];

/** 用户偏好“减少动态效果”：飞行、模式切换直接到位，不自动环绕。 */
export const reducedMotion = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** 自动画质：移动设备或低核心数 → 低；高核心数且高 DPR → 高；其余 → 中。 */
export function resolveQuality(q: Quality): EffectiveQuality {
  if (q !== "auto") return q;
  if (typeof navigator === "undefined") return "medium";
  const mobile = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
  const cores = navigator.hardwareConcurrency ?? 4, mem = (navigator as { deviceMemory?: number }).deviceMemory ?? 8;
  if (mobile || cores <= 4 || mem < 4) return "low";
  if (cores >= 8 && (typeof devicePixelRatio === "undefined" || devicePixelRatio >= 1.5)) return "high";
  return "medium";
}

function loadQuality(): Quality {
  try { const v = localStorage.getItem("solar-quality"); if (v === "low" || v === "medium" || v === "high" || v === "auto") return v; } catch { /* 忽略 */ }
  return "auto";
}
function loadLang(): Lang {
  try { const v = localStorage.getItem("solar-lang"); if (v === "en" || v === "zh") return v; } catch { /* 隐私模式等：退回中文 */ }
  if (typeof location !== "undefined" && /lang=en/.test(location.hash)) return "en";
  return "zh";
}

interface SceneState {
  /** 当前时刻（UTC 毫秒）。启动默认暂停、不选中（docs/UI设计.md「默认静止」）。 */
  timeMs: number;
  playing: boolean;
  /** 时间流速：天/秒（可为负）。 */
  speed: number;
  mode: Mode;
  selected: BodyId | null;
  hidden: Set<HideId>;
  quality: Quality;
  creditsOpen: boolean;
  layers: Layers;
  lang: Lang;
  /** 递增计数：相机据此执行“回到全景”。 */
  resetNonce: number;
  tick: (dtSec: number) => void;
  setTimeMs: (ms: number) => void;
  setPlaying: (p: boolean) => void;
  setSpeed: (d: number) => void;
  setMode: (m: Mode) => void;
  select: (id: BodyId | null) => void;
  setHidden: (ids: HideId[], hide: boolean) => void;
  setQuality: (q: Quality) => void;
  setCreditsOpen: (o: boolean) => void;
  toggleLayer: (k: keyof Layers) => void;
  setLang: (l: Lang) => void;
  resetView: () => void;
}

export const useScene = create<SceneState>((set, get) => ({
  timeMs: clamp(Date.now()),
  playing: false,
  speed: 10,
  mode: "model",
  selected: null,
  hidden: new Set(DEFAULT_HIDDEN),
  layers: { orbit: true, axis: true, label: true, moonOrbit: true, trail: true, auto: false },
  quality: loadQuality(),
  creditsOpen: false,
  lang: loadLang(),
  resetNonce: 0,
  tick: (dtSec) => {
    const s = get();
    if (!s.playing) return;
    const next = s.timeMs + dtSec * s.speed * 86400000;
    // 到达支持范围边界就停（不外推）
    if (next >= SUPPORTED_END || next <= SUPPORTED_START) set({ timeMs: clamp(next), playing: false });
    else set({ timeMs: next });
  },
  setTimeMs: (ms) => set({ timeMs: clamp(ms) }),
  setPlaying: (playing) => set({ playing }),
  setSpeed: (speed) => set({ speed }),
  setMode: (mode) => set({ mode }),
  select: (selected) => set({ selected }),
  setHidden: (ids, hide) =>
    set((s) => {
      const hidden = new Set(s.hidden);
      for (const id of ids) hide ? hidden.add(id) : hidden.delete(id);
      // 隐藏了选中的天体 → 取消选中（月球随地球显示）
      const isHidden = (id: BodyId) => hidden.has(id) || (id === "moon" && hidden.has("earth"));
      const sel = s.selected && isHidden(s.selected) ? null : s.selected;
      return { hidden, selected: sel };
    }),
  toggleLayer: (k) => set((s) => (k === "auto" && reducedMotion ? s : { layers: { ...s.layers, [k]: !s.layers[k] } })),
  setQuality: (quality) => { try { localStorage.setItem("solar-quality", quality); } catch { /* 忽略 */ } set({ quality }); },
  setCreditsOpen: (creditsOpen) => set({ creditsOpen }),
  setLang: (lang) => {
    try { localStorage.setItem("solar-lang", lang); } catch { /* 忽略 */ }
    set({ lang });
  },
  resetView: () => set((s) => ({ selected: null, resetNonce: s.resetNonce + 1 })),
}));

/** 节流订阅当前时间，供 UI 读数使用（场景内部直接读 getState，不触发 React 重渲染）。 */
export function useTimeMs(intervalMs = 100): number {
  const [t, setT] = useState(() => useScene.getState().timeMs);
  useEffect(() => {
    const id = setInterval(() => setT(useScene.getState().timeMs), intervalMs);
    setT(useScene.getState().timeMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return t;
}
